import React, { useState, useEffect, useCallback } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import config from '../config';

const DEFAULT_SENTENCES_API_URL = config.endpoints.sentences;

// ── Shuffle helper ─────────────────────────────────────────────────────────────
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ── Main component ─────────────────────────────────────────────────────────────
const SentenceFormation = ({ onNavigate }) => {
  const [allData, setAllData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameConfig, setGameConfig] = useState(null); // null = show setup
  const [level, setLevel] = useState('beginner');

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${DEFAULT_SENTENCES_API_URL}?level=${encodeURIComponent(level)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load sentences');
        return res.json();
      })
      .then(data => { setAllData(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [level]);

  return (
    <div className="home-container">
      <Header onNavigate={onNavigate} showHomeButton={false} />
      <div className="home-container">
        <div className="hero-section">
          <h1 className="hero-title">🧩 Sentence Formation</h1>
          <p className="hero-description">
            Arrange the word tiles to build a correct sentence. Take your time — there is no rush!
          </p>
        </div>
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div> Loading sentences…
          </div>
        )}
        {error && (
          <div className="error-container">
            ⚠️ {error}. Please check that the sentences API can access the configured server file.
          </div>
        )}
        {allData && !gameConfig && (
          <SetupScreen allData={allData} level={level} setLevel={setLevel} onStart={setGameConfig} />
        )}
        {allData && gameConfig && (
          <SentenceGame
            sentences={gameConfig.sentences}
            onBack={() => setGameConfig(null)}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

// ── Setup screen ───────────────────────────────────────────────────────────────
const SetupScreen = ({ allData, level, setLevel, onStart }) => {
  const [count, setCount] = useState(Math.min(10, allData.length));

  const available = allData;
  const maxCount = available.length;
  const sliderMax = Math.max(maxCount, 1);

  useEffect(() => {
    if (count > sliderMax) {
      setCount(sliderMax);
    } else if (count < 1) {
      setCount(1);
    }
  }, [sliderMax, count]);

  const handleStart = () => {
    const selected = shuffle(available).slice(0, Math.min(count, maxCount));
    onStart({ sentences: selected });
  };

  return (
    <div className="quiz-card sf-setup">
      <h2 className="section-title">⚙️ Set Up Your Activity</h2>

      <div className="sf-setup-form">
        <div className="sf-setup-field">
          <label htmlFor="sf-level">📚 Level</label>
          <select
            id="sf-level"
            className="form-select"
            value={level}
            onChange={e => setLevel(e.target.value)}
          >
            <option value="beginner">Beginner</option>
            <option value="elementary">Elementary</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
            <option value="master">Master</option>
          </select>
        </div>

        {/* Number of sentences */}
        <div className="sf-setup-field">
          <label htmlFor="sf-count">🔢 Number of sentences</label>
          <div className="sf-slider-row">
            <input
              id="sf-count"
              type="range"
              min={1}
              max={sliderMax}
              value={count}
              onChange={e => setCount(Number(e.target.value))}
              className="sf-slider"
            />
            <span className="sf-slider-value">{count}</span>
          </div>
          <span className="sf-age-group-label">{maxCount} sentences available</span>
        </div>
      </div>

      <button
        className="start-btn"
        style={{ marginTop: '30px', fontSize: '1.5rem', padding: '18px 50px' }}
        onClick={handleStart}
        disabled={maxCount === 0}
      >
        🚀 Start Activity
      </button>
    </div>
  );
};

// ── Game component ─────────────────────────────────────────────────────────────
const SentenceGame = ({ sentences, onBack }) => {
  const [index, setIndex] = useState(0);
  const [placed, setPlaced] = useState([]);
  const [pool, setPool] = useState(() => shuffle(sentences[0].words));
  const [feedback, setFeedback] = useState('');
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  const current = sentences[index];

  const loadSentence = useCallback((i) => {
    setPlaced([]);
    setPool(shuffle(sentences[i].words));
    setFeedback('');
    setShowAnswer(false);
  }, [sentences]);

  const handleWordTap = (word, idx) => {
    // Move from pool → placed
    const newPool = [...pool];
    newPool.splice(idx, 1);
    setPool(newPool);
    setPlaced(prev => [...prev, word]);
  };

  const handlePlacedTap = (word, idx) => {
    // Move from placed → pool
    const newPlaced = [...placed];
    newPlaced.splice(idx, 1);
    setPlaced(newPlaced);
    setPool(prev => [...prev, word]);
  };

  const handleCheck = () => {
    const correct = current.words.join(' ');
    const attempt = placed.join(' ');
    if (attempt === correct) {
      setScore(s => s + 1);
      setFeedback('correct');
    } else {
      setFeedback('incorrect');
    }
  };

  const handleNext = () => {
    if (index + 1 >= sentences.length) {
      setDone(true);
    } else {
      const next = index + 1;
      setIndex(next);
      loadSentence(next);
    }
  };

  const handleReset = () => {
    setIndex(0);
    setScore(0);
    setDone(false);
    setPlaced([]);
    setPool(shuffle(sentences[0].words));
    setFeedback('');
  };

  const handleClear = () => {
    setPool(prev => [...prev, ...placed]);
    setPlaced([]);
    setFeedback('');
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  if (done) {
    return (
      <div className="quiz-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '5rem', marginBottom: '20px' }}>🎉</div>
        <h2 className="section-title">You finished all sentences!</h2>
        <p style={{ fontSize: '1.8rem', color: '#667eea', fontWeight: 'bold', margin: '20px 0' }}>
          Final Score: {score} / {sentences.length}
        </p>
        <div className="sf-actions">
          <button className="start-btn" onClick={handleReset}>
            🔄 Play Again
          </button>
          <button className="help-btn" onClick={onBack}>
            ⚙️ Change Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-card">
      {/* Progress & Score */}
      <div className="sf-meta">
        <span className="sf-progress">
          Sentence {index + 1} of {sentences.length}
        </span>
        <span className="sf-score">⭐ Score: {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>
          ⚙️ Restart
        </button>
      </div>

      {/* Hint */}
      <p className="sf-hint">{current.hint}</p>

      {/* Drop zone — placed words */}
      <div className="sf-dropzone" aria-label="Your sentence">
        {placed.length === 0 && (
          <span className="sf-placeholder">Tap words below to build your sentence…</span>
        )}
        {placed.map((word, i) => (
          <button
            key={i}
            className="sf-tile sf-tile-placed"
            onClick={() => handlePlacedTap(word, i)}
            aria-label={`Remove ${word}`}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Feedback */}
      {feedback === 'correct' && (
        <div className="feedback correct">✅ Correct! Great job!</div>
      )}
      {feedback === 'incorrect' && (
        <div className="feedback incorrect">❌ Not quite — try rearranging the words!</div>
      )}

      {/* Show answer */}
      {showAnswer && (
        <div className="sf-answer">
          💡 <strong>Answer:</strong> {current.words.join(' ')}
        </div>
      )}

      {/* Word pool */}
      <div className="sf-pool">
        {pool.map((word, i) => (
          <button
            key={i}
            className="sf-tile sf-tile-pool"
            onClick={() => handleWordTap(word, i)}
            aria-label={`Add ${word}`}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="sf-actions">
        <button className="help-btn" onClick={handleClear}>
          🔄 Clear
        </button>
        {!showAnswer && (
          <button className="help-btn" onClick={handleShowAnswer}>
            💡 Show Answer
          </button>
        )}
        <button
          className="check-btn"
          onClick={handleCheck}
          disabled={placed.length === 0}
        >
          ✔️ Check
        </button>
        {(feedback === 'correct' || showAnswer) && (
          <button className="start-btn" onClick={handleNext}>
            ➡️ Next
          </button>
        )}
      </div>
    </div>
  );
};

export default SentenceFormation;
